import { comparer, makeAutoObservable, reaction } from 'mobx';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { TotalBetPossibleValuesStatusType } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';

const GAMES_CHANGE_DEBOUNCE_MS = 300;

export class TotalBetPossibleValuesState {
    private requestedGameIds: Array<string> = [];

    public constructor(
        private readonly campaignStore: CampaignStore,
        private readonly getGameIds: () => Array<string>,
        private readonly isEnabled: () => boolean
    ) {
        makeAutoObservable(this);

        reaction(
            () => this.gameIds,
            (gameIds) => this.setRequestedGameIds(gameIds),
            { delay: GAMES_CHANGE_DEBOUNCE_MS, equals: comparer.structural }
        );
    }

    private get gameIds(): Array<string> {
        return [...this.getGameIds()].sort();
    }

    private setRequestedGameIds = (gameIds: Array<string>): void => {
        this.requestedGameIds = gameIds;
    };

    private get isIdle(): boolean {
        return this.isEnabled() === false || this.gameIds.length === 0;
    }

    public get status(): TotalBetPossibleValuesStatusType {
        if (this.isIdle) {
            return { type: 'idle' };
        }

        if (comparer.structural(this.requestedGameIds, this.gameIds) === false) {
            return { type: 'loading' };
        }

        const result = this.campaignStore.totalBetPossibleValuesResource.get(this.requestedGameIds).get();

        switch (result.type) {
            case 'ready':
                return { type: 'ready', value: result.value };
            case 'error':
                return { type: 'error', message: result.message };
            default:
                return { type: 'loading' };
        }
    }

    public get possibleValues(): Array<number> {
        const status = this.status;
        return status.type === 'ready' ? (status.value.possibleValues ?? []) : [];
    }

    /**
     * Skips the debounce and waits for the response for currently selected games.
     * A failed request is retried.
     */
    public ensureFresh = async (): Promise<void> => {
        this.setRequestedGameIds(this.gameIds);

        if (this.isIdle) {
            return;
        }

        const resource = this.campaignStore.totalBetPossibleValuesResource.get(this.requestedGameIds);
        const result = resource.get();

        if (result.type === 'error') {
            await resource.refresh();
        } else if (result.type === 'loading') {
            await result.whenReady;
        }
    };
}
